import React, { useState } from "react";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { useApp } from "../../contexts/AppContext";
import { db } from "../../utils/firebase";
import { PARTY_CODE_LENGTH } from "../../utils/partyCode";

import Button from "../Button/Button";

const LEGACY_CODE_LENGTH = 7;

const partiesRef = collection(db, "parties");

function JoinParty() {
  const {
    state: { user, event, globalUser },
    setSelectedPartyId,
  } = useApp();
  const [partyId, setPartyId] = useState("");
  const [name, setName] = useState(
    globalUser?.nickName ?? user?.displayName ?? ""
  );
  const [isJoining, setIsJoining] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const hasValidCode =
    partyId.length === PARTY_CODE_LENGTH ||
    partyId.length === LEGACY_CODE_LENGTH;
  const canJoin = hasValidCode && name.trim().length > 0;

  async function joinParty(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const raw = partyId.trim();
    const trimmedName = name.trim();
    if (
      !raw ||
      (raw.length !== PARTY_CODE_LENGTH && raw.length !== LEGACY_CODE_LENGTH) ||
      !trimmedName ||
      !user ||
      !event ||
      isJoining
    )
      return;

    setIsJoining(true);
    setErrorMessage("");

    // Accept both new string codes and legacy numeric codes stored as numbers.
    const candidates: (string | number)[] = [raw];
    if (/^\d+$/.test(raw)) candidates.push(parseInt(raw, 10));

    const q = query(partiesRef, where("shortId", "in", candidates));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.docs.length === 0) {
      setErrorMessage(`Unable to find a party with ID "${raw}"`);
      setIsJoining(false);
      return;
    }

    if (querySnapshot.docs.length > 1) {
      setErrorMessage(
        `Sorry, but we have a small hickup, please join another party`
      );
      setIsJoining(false);
      return;
    }

    const _party = querySnapshot.docs[0];

    // If already a member, just select the party.
    const existingMembership = await getDocs(
      query(
        collection(db, "partyUsers"),
        where("partyId", "==", _party.id),
        where("userId", "==", user.uid),
        where("active", "==", true)
      )
    );

    if (existingMembership.docs.length === 0) {
      const partyUser = {
        active: true,
        userId: user.uid,
        name: trimmedName || globalUser?.nickName || user.displayName,
        partyId: _party.id,
        eventId: event._docId,
        created: serverTimestamp(),
      };
      await addDoc(collection(db, "partyUsers"), partyUser);
    }

    setSelectedPartyId(_party.id);
    setIsJoining(false);
  }

  return (
    <>
      <p className="text-center text-lg mb-4">Join a party and show the world your Eurovision skills!</p>
      <form
        className="mt-6 flex flex-col items-center"
        autoComplete="off"
        onSubmit={joinParty}
      >
        <div className="mb-4">
        <label
            className="block text-lg text-esc-neutral font-medium mb-2"
            htmlFor="joinname"
          >
            Party ID
          </label>
          <input
            id="partyid"
            className="text-center appearance-none text-2xl tracking-widest border w-full py-2 px-3 text-esc-secondary leading-tight focus:outline-none focus:shadow-outline"
            type="text"
            name="id"
            value={partyId}
            onChange={(event) => {
              setErrorMessage("");
              setPartyId(event.target.value.trim());
            }}
            placeholder="Eg. '1234567'"
            maxLength={LEGACY_CODE_LENGTH}
          />
        </div>
        <div className="mb-4 w-full">
          <label
            className="block text-lg text-esc-neutral font-medium mb-2"
            htmlFor="joinname"
          >
            Your name in this party
          </label>
          <input
            id="joinname"
            className="text-xl text-esc-secondary appearance-none border w-full py-2 px-3 text-esc-neutral leading-tight focus:outline-none focus:shadow-outline"
            type="text"
            name="joinname"
            value={name}
            onChange={(event) => {
              setErrorMessage("");
              setName(event.target.value);
            }}
            placeholder="Your name"
            maxLength={23}
          />
        </div>
        <Button
          type="submit"
          variant="primary"
          disabled={!canJoin || isJoining}
          spinner={isJoining}
          size="xl"
          className="shadow-xl"
        >
          {isJoining ? "Joining" : "Join"}
        </Button>

        <div className="mt-4 text-esc-red-200 text-lg text-center">
          {errorMessage && <span>{errorMessage}</span>}
          {!errorMessage && <span>&nbsp;</span>}
        </div>
      </form>
    </>
  );
}

export default JoinParty;
